import { Box, Text, Tooltip, useClipboard, useToast } from "@chakra-ui/react";

/**
 * Listing-page name cell. Renders the entity name with the description
 * as small italic subtitle below, replacing the separate Description
 * column. Hover shows the entity ID in a tooltip; click copies the ID
 * to the clipboard.
 *
 *   <NameWithDescriptionCell
 *     name={item.name}
 *     description={item.description}
 *     id={item.id}
 *   />
 *
 * Stop-event-propagation on the click so row-level click handlers
 * (navigate-to-detail) don't fire when the user is just copying an id.
 */
export default function NameWithDescriptionCell({ name, description, id }) {
  const { onCopy } = useClipboard(id || "");
  const toast = useToast();

  const handleClick = (e) => {
    if (!id) return;
    e.stopPropagation();
    onCopy();
    toast({
      title: "ID copied",
      description: id,
      status: "success",
      duration: 1500,
      isClosable: true,
      position: "bottom-right",
    });
  };

  const tooltipLabel = id ? `ID: ${id} — click to copy` : name;

  return (
    <Tooltip label={tooltipLabel} placement="top-start" openDelay={300} hasArrow>
      <Box
        onClick={handleClick}
        cursor={id ? "pointer" : "default"}
        py={1}
        _hover={id ? { bg: "blackAlpha.50" } : undefined}
        borderRadius="sm"
        px={2}
        ml={-2}
      >
        <Text fontWeight="medium" noOfLines={1}>
          {name || "—"}
        </Text>
        {description && (
          <Text
            fontSize="xs"
            color="gray.500"
            fontStyle="italic"
            noOfLines={1}
          >
            {description}
          </Text>
        )}
      </Box>
    </Tooltip>
  );
}
